import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, MessageCircle, ChefHat, Coffee, Building2, Palmtree, Cross, Wheat, Shirt, Utensils, ShoppingCart, Cloud, Factory, ShoppingBag, Truck, Shield, Lock, Camera, Crown } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { WHATSAPP_URL, SHOP_URL } from "../lib/i18n";
import { HeroChips } from "../components/HeroChips";
import { ClientsStrip } from "../components/ClientsStrip";
import { BusinessHub } from "../components/BusinessHub";
import { PackagesSection } from "../components/PackagesSection";
import { WhyNumbered } from "../components/WhyNumbered";
import { CostEstimator } from "../components/CostEstimator";
import { StainlessFactory } from "../components/StainlessFactory";
import { GlobalReach } from "../components/GlobalReach";
import { MaintenancePromo } from "../components/MaintenancePromo";
import { BrandsMarquee } from "../components/BrandsMarquee";
import { TestimonialsSection } from "../components/TestimonialsSection";
import { FinalCTA } from "../components/FinalCTA";
import { useSiteImages } from "../hooks/use-site-images";

const HERO_IMG = "https://images.unsplash.com/photo-1749478072094-d21cb929490c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwa2l0Y2hlbiUyMHN0YWlubGVzcyUyMHN0ZWVsfGVufDB8fHx8MTc4MjMxOTA3OXww&ixlib=rb-4.1.0&q=85";

const CATEGORY_ICONS = {
  restaurant: Utensils, cafe: Coffee, hotel: Building2, resort: Palmtree,
  villa_palace: Crown,
  hospital: Cross, bakery: Wheat, laundry: Shirt, catering: ChefHat,
  supermarket: ShoppingCart, cloud_kitchen: Cloud, central_kitchen: Factory,
};

export default function Home() {
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [featured, setFeatured] = useState([]);
  const [blog, setBlog] = useState([]);
  const siteImage = useSiteImages();

  useEffect(() => {
  api.get("/projects?featured=true")
    .then((r) => {
      console.log("Projects Response:", r.data);

      const projects = Array.isArray(r.data)
        ? r.data
        : Array.isArray(r.data?.data)
        ? r.data.data
        : [];

      setFeatured(projects.slice(0, 6));
    })
    .catch((err) => {
      console.error("Projects API Error:", err);
      setFeatured([]);
    });

  api.get("/blog?limit=3")
    .then((r) => {
      console.log("Blog Response:", r.data);

      const blogs = Array.isArray(r.data)
        ? r.data
        : Array.isArray(r.data?.data)
        ? r.data.data
        : [];

      setBlog(blogs);
    })
    .catch((err) => {
      console.error("Blog API Error:", err);
      setBlog([]);
    });

}, []);

  const stats = [
    { value: "20+", label: t.stats.years },
    { value: "500+", label: t.stats.projects },
    { value: "40+", label: t.stats.brands },
    { value: "7", label: t.stats.emirates },
  ];


  console.log("rotatingWords:", t.hero.rotatingWords, Array.isArray(t.hero.rotatingWords));
console.log("categories:", t.categories);
console.log("featured:", featured, Array.isArray(featured));
console.log("blog:", blog, Array.isArray(blog));


  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={siteImage("home_hero", HERO_IMG)} alt="Commercial kitchen" className="w-full h-full object-cover" />
          <div className="absolute inset-0 brand-bg-hero" />
          {/* Floating brand orbs */}
          <div className="absolute top-[10%] end-[8%] w-72 h-72 rounded-full bg-[#0891b2]/25 blur-3xl orb-float pointer-events-none" />
          <div className="absolute bottom-[15%] start-[12%] w-96 h-96 rounded-full bg-[#ef4444]/20 blur-3xl orb-float delay-1 pointer-events-none" />
          <div className="absolute top-[45%] start-[45%] w-64 h-64 rounded-full bg-amber-300/15 blur-3xl orb-float delay-2 pointer-events-none" />
        </div>
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <p className="text-[11px] tracking-[0.3em] text-red-200 mb-5 font-semibold reveal-up" data-testid="hero-eyebrow" style={{ animationDelay: "0s" }}>
            {t.hero.eyebrow}
          </p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.15] max-w-4xl mb-6 reveal-up" data-testid="hero-title" style={{ animationDelay: "0.15s" }}>
            <span className="block">{t.hero.titleLead}</span>
            <span className="block mt-2">
              <span className="text-white/85">{t.hero.titleForConnector} </span>
              <span
                className="word-rotator text-[#67e8f9] font-display"
                data-widest={t.hero.rotatingWords[3]}
              >
                {t.hero.rotatingWords.map((w, i) => (
                  <span key={i}>{w}</span>
                ))}
              </span>
            </span>
          </h1>
          <p className="text-white/85 text-base md:text-lg max-w-2xl mb-8 leading-relaxed reveal-up" style={{ animationDelay: "0.3s" }}>
            {t.hero.subtitle}
          </p>
          <div className="flex flex-wrap gap-3 reveal-up" style={{ animationDelay: "0.45s" }}>
            <Link to="/quote">
              <Button data-testid="hero-cta-quote" className="btn-shine bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-6 text-sm font-semibold shadow-lg">
                {t.hero.cta1} <Arrow className="w-4 h-4 ms-2" />
              </Button>
            </Link>
            <Link to="/projects">
              <Button variant="outline" data-testid="hero-cta-projects" className="border-white/60 bg-white/10 hover:bg-white text-white hover:text-[#0891b2] h-12 px-6 text-sm font-semibold backdrop-blur">
                {t.hero.cta2}
              </Button>
            </Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
              <Button data-testid="hero-cta-whatsapp" className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 text-sm font-semibold">
                <MessageCircle className="w-4 h-4 me-2" /> {t.hero.cta3}
              </Button>
            </a>
          </div>
          <HeroChips />
        </div>
      </section>

      {/* CLIENTS TRUSTED BY */}
      <ClientsStrip />

      {/* STATS */}
      <section className="bg-[#0891b2] text-white py-10" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <div className="font-display text-4xl lg:text-5xl mb-1">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-cyan-200">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20" data-testid="intro-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] mb-5">{t.intro.title}</h2>
          <p className="text-slate-600 leading-relaxed text-lg">{t.intro.body}</p>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 bg-slate-50" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] mb-3">{t.categoriesSection.title}</h2>
            <p className="text-slate-600">{t.categoriesSection.subtitle}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(t.categories).map(([key, label], i) => {
              const Icon = CATEGORY_ICONS[key] || Utensils;
              return (
                <Link
                  key={key}
                  to={`/projects?category=${key}`}
                  data-testid={`cat-${key}`}
                  className="group bg-white border border-slate-200 hover:border-[#0891b2] p-5 rounded-lg text-center transition-colors"
                >
                  <div className="w-12 h-12 mx-auto bg-cyan-50 group-hover:bg-[#0891b2] rounded-full flex items-center justify-center mb-3 transition-colors">
                    <Icon className="w-6 h-6 text-[#0891b2] group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-sm font-medium text-slate-700 group-hover:text-[#0891b2] transition-colors">{label}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BUSINESS HUB — dark themed */}
      <BusinessHub />

      {/* FEATURED PROJECTS with photo count */}
      {featured.length > 0 && (
        <section className="py-20" data-testid="featured-section">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
                  {lang === "ar" ? "أعمالنا" : "Our Work"}
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0891b2] mb-2 leading-tight">{t.featured.title}</h2>
                <p className="text-slate-600">{t.featured.subtitle}</p>
              </div>
              <Link to="/projects" className="text-[#ef4444] text-sm font-medium hover:gap-3 inline-flex items-center gap-2 transition-all" data-testid="featured-all">
                {t.featured.viewAll} <Arrow className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((p, i) => {
                const photoCount = (p.gallery?.length || 0) + (p.image ? 1 : 0);
                return (
                  <Link key={p.id} to={`/projects/${p.slug}`} data-testid={`featured-${i}`}
                        className="group bg-white border border-slate-200 hover:border-[#0891b2] hover:shadow-xl transition-all rounded-lg overflow-hidden">
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                      <img src={p.image} alt={p.title_en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {photoCount > 1 && (
                        <div className="absolute bottom-3 end-3 bg-[#0f172a]/85 backdrop-blur-sm text-white text-[11px] px-2.5 py-1 rounded flex items-center gap-1.5 font-medium">
                          <Camera className="w-3 h-3" />
                          {photoCount} {t.projectExtras.photos}
                        </div>
                      )}
                      <div className="absolute top-3 start-3 bg-white/95 text-[#0891b2] text-[10px] uppercase tracking-widest px-2.5 py-1 rounded font-semibold">
                        {t.categories[p.category]}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg text-slate-900 mb-2 line-clamp-2">{lang === "ar" ? p.title_ar : p.title_en}</h3>
                      <p className="text-slate-600 text-sm line-clamp-2">{lang === "ar" ? p.description_ar : p.description_en}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* WHY MARIOT — numbered + stats */}
      <WhyNumbered />

      {/* STAINLESS STEEL FACTORY */}
      <StainlessFactory />

      {/* GLOBAL REACH — Serving GCC & exporting worldwide */}
      <GlobalReach />

      {/* MAINTENANCE PROMO */}
      <MaintenancePromo />

      {/* COST ESTIMATOR */}
      <CostEstimator />

      {/* PACKAGES */}
      <PackagesSection />

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* BRANDS MARQUEE */}
      <BrandsMarquee />

      {/* BLOG PREVIEW */}
      {blog.length > 0 && (
        <section className="py-20 bg-white" data-testid="blog-preview">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
                  {lang === "ar" ? "أفكار وأدلة" : "Insights & Guides"}
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0891b2] mb-2 leading-tight">{t.blog.title}</h2>
                <p className="text-slate-600">{t.blog.subtitle}</p>
              </div>
              <Link to="/blog" className="text-[#ef4444] text-sm font-medium hover:gap-3 inline-flex items-center gap-2 transition-all" data-testid="blog-all">
                {t.featured.viewAll} <Arrow className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {blog.map((b, i) => (
                <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`blog-preview-${i}`}
                      className="bg-white rounded-lg overflow-hidden border border-slate-200 hover:border-[#0891b2] hover:shadow-lg transition-all">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={b.featured_image} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-widest text-[#ef4444] mb-2">{b.category}</div>
                    <h3 className="font-display text-lg text-slate-900 line-clamp-2">{lang === "ar" ? b.title_ar : b.title_en}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SHOP CTA */}
      <section className="py-20 bg-slate-50" data-testid="home-shop-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-8 lg:p-12">
              <div className="inline-flex items-center gap-2 bg-cyan-50 text-[#0891b2] rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest mb-4">
                <ShoppingBag className="w-3.5 h-3.5" />
                {t.shopSection.eyebrow}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#334155] mb-4">{t.shopSection.title}</h2>
              <p className="text-slate-600 leading-relaxed mb-6">{t.shopSection.subtitle}</p>
              <div className="grid grid-cols-3 gap-3 mb-8">
                {[
                  { icon: Truck, label: t.shopSection.badge1 },
                  { icon: Shield, label: t.shopSection.badge2 },
                  { icon: Lock, label: t.shopSection.badge3 },
                ].map((b, i) => (
                  <div key={i} className="flex flex-col items-center gap-2 text-center" data-testid={`shop-badge-${i}`}>
                    <div className="w-10 h-10 bg-cyan-50 rounded-full flex items-center justify-center">
                      <b.icon className="w-5 h-5 text-[#ef4444]" />
                    </div>
                    <span className="text-xs text-slate-600 font-medium">{b.label}</span>
                  </div>
                ))}
              </div>
              <a href={SHOP_URL} target="_blank" rel="noreferrer">
                <Button data-testid="home-shop-btn" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-8 font-semibold">
                  <ShoppingBag className="w-4 h-4 me-2" /> {t.shopSection.button} <Arrow className="w-4 h-4 ms-2" />
                </Button>
              </a>
              <p className="text-xs text-slate-500 mt-3 font-mono" dir="ltr">mariotstore.com</p>
            </div>
            <div className="relative aspect-square lg:aspect-auto lg:h-full min-h-[320px] bg-gradient-to-br from-[#0891b2] to-[#334155]">
              <img src={siteImage("home_shop", "https://images.pexels.com/photos/2977514/pexels-photo-2977514.jpeg")} alt="Shop" className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-70" />
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="text-center text-white">
                  <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-80" />
                  <div className="font-display text-2xl mb-1">Mariot Store</div>
                  <div className="text-white/80 text-sm font-mono" dir="ltr">mariotstore.com</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL BIG CTA */}
      <FinalCTA />
    </div>
  );
}
